"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTreeSource = void 0;
const common_types_1 = require("../../common-types");
const react_1 = require("react");
const react_dom_1 = require("react-dom");
const react_intl_1 = require("react-intl");
const lodash_1 = require("lodash");
const snackbar_context_1 = require("../../composite/snackbar/snackbar-context");
const snackbar_types_1 = require("../../composite/snackbar/snackbar-types");
const error_utils_1 = require("../../utils/error-utils");
const event_callback_hook_1 = require("../../utils/event-callback-hook");
const serialize_call_hook_1 = require("../../utils/serialize-call-hook");
const tree_api_1 = require("./tree-api");
const tree_utils_1 = require("./tree-utils");
/**
 * Manages state for an immutable tree data structure, and provides convenience methods to
 * update the data over time.
 */
function useTreeSource(options) {
    const { url, rootId = null, initialItems = [], initialSelectedKeys, getKey = (item) => item.id || item.key, getChildren = (item) => item.children, } = options;
    const fetch = (0, react_1.useRef)(null);
    const urlRef = (0, react_1.useRef)(url);
    const dataRef = (0, react_1.useRef)({
        items: [],
        count: 0,
    });
    const isDataValidRef = (0, react_1.useRef)(false);
    const hasNextPageRef = (0, react_1.useRef)(true);
    const parentKeyRef = (0, react_1.useRef)();
    const intl = (0, react_intl_1.useIntl)();
    const { showSnackbar } = (0, react_1.useContext)(snackbar_context_1.SnackbarContext);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const setSearchQuery = (0, event_callback_hook_1.useEventCallback)((q) => {
        urlRef.current = `${url}/autocomplete?query=${encodeURIComponent(q)}`;
    });
    const map = (0, react_1.useMemo)(() => new Map(), []);
    // We only want to compute this on initial render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const initialNodes = (0, react_1.useMemo)(() => buildTree(initialItems), []);
    const [items, setItems] = (0, react_1.useState)(initialNodes);
    const [count, setCount] = (0, react_1.useState)(0);
    const [selectedKeys, setSelectedKeys] = (0, react_1.useState)(new Set(initialSelectedKeys || []));
    (0, react_1.useEffect)(() => {
        if (url) {
            urlRef.current = url;
        }
    }, [url]);
    function buildPath(items) {
        if (items[0].parent) {
            const parentNode = map.get(items[0].parent.id);
            if (parentNode) {
                append(parentNode.key, ...items);
            }
            else {
                buildPath([items[0].parent]);
                append(items[0].parent.id, ...items);
            }
        }
        else {
            append(null, ...items);
        }
    }
    const loadMoreUnsafe = (0, event_callback_hook_1.useEventCallback)(async () => {
        try {
            if (isDataValidRef.current && !hasNextPageRef.current) {
                // skip
                return dataRef.current;
            }
            if (!urlRef.current) {
                console.error('Can not load treeSource without valid URL');
            }
            setLoading(true);
            const filters = [];
            if (parentKeyRef.current || rootId) {
                filters.push({
                    field: 'parent.id',
                    operation: common_types_1.ApiFilterOperation.EQ,
                    value: parentKeyRef.current || rootId,
                });
            }
            const combinedParams = {
                sort: [
                    {
                        field: 'hierarchicalOrder',
                        type: 'FIELD',
                        order: 'ASC',
                    },
                ],
                filters,
                size: 30,
                searchAfter: isDataValidRef.current
                    ? dataRef.current.searchAfter
                    : undefined,
            };
            fetch.current = (0, tree_api_1.listTreeData)(urlRef.current, combinedParams);
            const data = await fetch.current.json();
            if (rootId && parentKeyRef.current) {
                if (data.items.length) {
                    insert(parentKeyRef.current === rootId ? null : parentKeyRef.current, 0, ...data.items);
                }
            }
            else {
                const groups = (0, lodash_1.groupBy)(data.items, (item) => { var _a; return (_a = item.parent) === null || _a === void 0 ? void 0 : _a.id; });
                Object.values(groups).map((group) => {
                    buildPath(group);
                });
                if (!isDataValidRef.current) {
                    dataRef.current = data;
                }
                else {
                    dataRef.current = {
                        ...data,
                        items: [...dataRef.current.items, ...data.items],
                    };
                }
                isDataValidRef.current = true;
                hasNextPageRef.current = data.count > dataRef.current.items.length;
                (0, react_dom_1.unstable_batchedUpdates)(() => {
                    setCount(data.count);
                    setLoading(false);
                });
                fetch.current = null;
            }
        }
        catch (err) {
            if (err.name !== 'AbortError') {
                isDataValidRef.current = true;
                hasNextPageRef.current = false;
                const errorDetail = (0, error_utils_1.getErrorMessage)(err);
                const message = intl.formatMessage({
                    id: 'EAS_SCROLLABLE_SOURCE_MSG_ERROR',
                    defaultMessage: 'Chyba načtení dat: {detail}',
                }, { detail: errorDetail });
                setLoading(false);
                showSnackbar(message, snackbar_types_1.SnackbarVariant.ERROR);
            }
        }
        return dataRef.current;
    });
    const [loadMore] = (0, serialize_call_hook_1.useSerializeCall)(loadMoreUnsafe);
    function buildTree(initialItems = [], parentKey) {
        return initialItems.map((item) => {
            const node = {
                key: getKey(item),
                parentKey: parentKey,
                value: item,
                children: null,
            };
            node.children = buildTree(getChildren(item), node.key);
            map.set(node.key, node);
            return node;
        });
    }
    function updateTree(items, key, update) {
        var _a, _b;
        let node = map.get(key);
        if (!node) {
            return items;
        }
        // Create a new node. If null, then delete the node, otherwise replace.
        let newNode = update(node);
        if (newNode == null) {
            deleteNode(node);
        }
        else {
            addNode(newNode);
        }
        // Walk up the tree and update each parent to refer to the new chilren.
        while (node.parentKey) {
            const nextParent = map.get(node.parentKey);
            const copy = {
                key: nextParent.key,
                parentKey: nextParent.parentKey,
                value: nextParent.value,
                children: null,
            };
            let children = nextParent.children;
            if (newNode == null) {
                children = (_a = children === null || children === void 0 ? void 0 : children.filter((c) => c !== node)) !== null && _a !== void 0 ? _a : null;
            }
            copy.children =
                (_b = children === null || children === void 0 ? void 0 : children.map((child) => {
                    if (child === node) {
                        return newNode;
                    }
                    return child;
                })) !== null && _b !== void 0 ? _b : null;
            map.set(copy.key, copy);
            newNode = copy;
            node = nextParent;
        }
        if (newNode == null) {
            items = items.filter((c) => c !== node);
        }
        return items.map((item) => {
            if (item === node) {
                return newNode;
            }
            return item;
        });
    }
    function addNode(node) {
        map.set(node.key, node);
        if (node.children) {
            for (const child of node.children) {
                addNode(child);
            }
        }
    }
    function deleteNode(node) {
        map.delete(node.key);
        if (node.children) {
            for (const child of node.children) {
                deleteNode(child);
            }
        }
    }
    function getItem(key) {
        return map.get(key);
    }
    async function insertAsync(parentKey, value) {
        var _a, _b, _c, _d;
        if (parentKey) {
            const parentNode = map.get(parentKey);
            const response = await (0, tree_api_1.addTreeNode)(url, (_a = parentKey === null || parentKey === void 0 ? void 0 : parentKey.toString()) !== null && _a !== void 0 ? _a : null, value).json();
            const newItem = {
                id: response.id,
                label: response.label,
                version: response.version,
                childrenCount: response.children.length,
            };
            if (parentNode) {
                parentNode.value.childrenCount++;
            }
            insert((_b = parentNode === null || parentNode === void 0 ? void 0 : parentNode.key) !== null && _b !== void 0 ? _b : null, (_d = (_c = parentNode === null || parentNode === void 0 ? void 0 : parentNode.children) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0, newItem);
        }
    }
    function insert(parentKey, index, ...values) {
        setItems((items) => {
            const nodes = buildTree(values, parentKey);
            // If parentKey is null, insert into the root.
            if (parentKey == null) {
                return [...items.slice(0, index), ...nodes, ...items.slice(index)];
            }
            // Otherwise, update the parent node and its ancestors.
            return updateTree(items, parentKey, (parentNode) => ({
                key: parentNode.key,
                parentKey: parentNode.parentKey,
                value: parentNode.value,
                children: [
                    ...parentNode.children.slice(0, index),
                    ...nodes,
                    ...parentNode.children.slice(index),
                ],
            }));
        });
    }
    function insertBefore(key, ...values) {
        const node = map.get(key);
        if (!node) {
            return;
        }
        const parentNode = map.get(node.parentKey);
        const nodes = parentNode ? parentNode.children : items;
        const index = nodes.indexOf(node);
        insert(parentNode.key, index, ...values);
    }
    function insertAfter(key, ...values) {
        const node = map.get(key);
        if (!node) {
            return;
        }
        const parentNode = map.get(node.parentKey);
        const nodes = parentNode ? parentNode.children : items;
        const index = nodes.indexOf(node);
        insert(parentNode.key, index + 1, ...values);
    }
    function prepend(parentKey, ...values) {
        insert(parentKey, 0, ...values);
    }
    function append(parentKey, ...values) {
        if (parentKey == null) {
            insert(null, items.length, ...values);
        }
        else {
            const parentNode = map.get(parentKey);
            if (!parentNode) {
                return;
            }
            insert(parentKey, parentNode.children.length, ...values);
        }
    }
    function remove(...keys) {
        let newItems = items;
        for (const key of keys) {
            newItems = updateTree(newItems, key, () => null);
        }
        setItems(newItems);
        const selection = new Set(selectedKeys);
        for (const key of selectedKeys) {
            if (!map.has(key)) {
                selection.delete(key);
            }
        }
        setSelectedKeys(selection);
    }
    function removeSelectedItems() {
        remove(...selectedKeys);
    }
    function move(key, toParentKey, index) {
        setItems((items) => {
            const node = map.get(key);
            if (!node) {
                return items;
            }
            items = updateTree(items, key, () => null);
            const movedNode = {
                ...node,
                parentKey: toParentKey,
            };
            return updateTree(items, toParentKey, (parentNode) => ({
                key: parentNode.key,
                parentKey: parentNode.parentKey,
                value: parentNode.value,
                children: [
                    ...parentNode.children.slice(0, index),
                    movedNode,
                    ...parentNode.children.slice(index),
                ],
            }));
        });
    }
    function update(oldKey, newValue, copyChildren = false) {
        setItems((items) => updateTree(items, oldKey, (oldNode) => {
            const node = {
                key: oldNode.key,
                parentKey: oldNode.parentKey,
                value: newValue,
                children: copyChildren ? oldNode.children : null,
            };
            if (!copyChildren) {
                node.children = buildTree(getChildren(newValue), node.key);
            }
            return node;
        }));
    }
    async function updateAsync(oldKey, newValue) {
        var _a;
        const node = map.get(oldKey);
        const parentId = node === null || node === void 0 ? void 0 : node.parentKey;
        const response = await (0, tree_api_1.updateTreeNode)(url, (_a = parentId === null || parentId === void 0 ? void 0 : parentId.toString()) !== null && _a !== void 0 ? _a : rootId, newValue).json();
        const newItem = {
            id: response.id,
            label: response.label,
            version: response.version,
            childrenCount: response.children.length,
        };
        update(oldKey, newItem, true);
    }
    function reset(parent) {
        (0, react_dom_1.unstable_batchedUpdates)(() => {
            var _a;
            parentKeyRef.current = parent;
            (_a = fetch.current) === null || _a === void 0 ? void 0 : _a.abort();
            map.clear();
            hasNextPageRef.current = true;
            isDataValidRef.current = false;
            setLoading(false);
            setItems([]);
            setCount(0);
        });
    }
    function* treeWalker(handleSelect) {
        // Step [1]: Define the root node of our tree. There can be one or
        // multiple nodes.
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const selected = selectedKeys.has(item.key);
            yield (0, tree_utils_1.getNodeData)({
                node: item,
                nestingLevel: 0,
                selected,
                select: handleSelect,
                download: async (key) => {
                    parentKeyRef.current = key;
                    loadMore();
                },
                isOpenByDefault: !rootId,
            });
        }
        while (true) {
            // Step [2]: Get the parent component back. It will be the object
            // the `getNodeData` function constructed, so you can read any data from it.
            const parent = yield;
            const parentNode = getItem(parent.node.id);
            if (parentNode === null || parentNode === void 0 ? void 0 : parentNode.children) {
                for (let i = 0; i < parentNode.children.length; i++) {
                    // Step [3]: Yielding all the children of the provided component. Then we
                    // will return for the step [2] with the first children.
                    const node = parentNode.children[i];
                    const selected = selectedKeys.has(node.key);
                    yield (0, tree_utils_1.getNodeData)({
                        node,
                        nestingLevel: parent.nestingLevel + 1,
                        selected,
                        select: handleSelect,
                        download: async (key) => {
                            parentKeyRef.current = key;
                            loadMore();
                        },
                        isOpenByDefault: !rootId,
                    });
                }
            }
        }
    }
    return {
        rootId,
        count,
        loading,
        items,
        selectedKeys,
        setSelectedKeys,
        setSearchQuery,
        getItem,
        loadMore,
        insert,
        insertAsync,
        insertBefore,
        insertAfter,
        prepend,
        append,
        remove,
        removeSelectedItems,
        move,
        update,
        updateAsync,
        reset,
        treeWalker,
    };
}
exports.useTreeSource = useTreeSource;
//# sourceMappingURL=tree-source-hook.js.map